import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { defaultSortComparator } from '@components/DataGrid/types';
import RenderHeader from '@components/DataGrid/RenderHeader';
import { HeaderSubLabel, SubColumnDef, subColumnField } from '@components/DataGrid/subColumns';
import DocumentsLink from '@components/DocumentsLink';
import ChargesAndPaymentsLink from '@components/ChargesAndPaymentsLink';
import RenderCell from '@components/DataGrid/RenderCell';
import MailLink from '@components/MailLink';
import PhoneLink, { formatPhoneNumber } from '@components/PhoneLink';
import { applyColumnQuickFilter, caseInsensitiveIncludes } from '../columns';
import { GridColDefExtended } from '@mui/x-data-grid-pro';

import styles from '../../RenderCell.module.css';

const customerField = 'Customer';

/** Sub-values of the Customer column sortable via hidden companion columns */
const customerSubColumns: SubColumnDef<DispatchLeg>[] = [{ key: 'ref', label: 'Customer Ref #', valueGetter: (row) => row.csnCustRefNo }];

const customerSubLabels: HeaderSubLabel[] = customerSubColumns.map((sub) => ({
	label: sub.label,
	field: subColumnField(customerField, sub.key)
}));

const customerDefinition: GridColDefExtended<DispatchLeg> = {
	field: customerField,
	minWidth: 75,
	width: 200,
	headerName: 'Customer|||Cust Ref #',
	subColumns: customerSubColumns,
	/** Mobile cards invoke renderHeader without params (RenderCardCell), so they fall back to the
	 * static secondaryText instead of mounting the grid-subscribing sort labels per card */
	renderHeader: (params) => (
		<RenderHeader
			primaryText='Customer'
			subLabels={params.field ? customerSubLabels : undefined}
			secondaryText='Customer Ref #'
		/>
	),
	renderCell: ({ row }) => {
		return (
			<RenderCell
				primarySlot={
					row.custName && (
						<span
							className={styles.bold}
							title={row.custName}
						>
							{row.custName}
						</span>
					)
				}
				secondarySlot={
					row.csnCustRefNo && (
						<span
							className={styles['secondary-text']}
							title={row.csnCustRefNo}
						>
							{row.csnCustRefNo}
						</span>
					)
				}
				tertiarySlot={
					row.custId && (
						<div className={styles['cell-actions']}>
							<div className={styles['cell-action']}>
								<DocumentsLink leg={row} />
							</div>
							<div className={styles['cell-action']}>
								<ChargesAndPaymentsLink leg={row} />
							</div>
							<div className={styles['cell-action']}>
								{row.woContactEmail && (
									<MailLink
										emailAddress={row.woContactEmail}
										showIcon={true}
										title={row.woContactName ? `${row.woContactName}: ${row.woContactEmail}` : undefined}
									/>
								)}
							</div>
							<div className={styles['cell-action']}>
								{row.woContactPhone && (
									<PhoneLink
										phoneNumber={row.woContactPhone}
										showIcon={true}
										title={row.woContactName ? `${row.woContactName}: ${formatPhoneNumber(row.woContactPhone) ?? ''}` : undefined}
									/>
								)}
							</div>
						</div>
					)
				}
			/>
		);
	},
	disableReorder: true,
	getApplyQuickFilterFn: (value: string) => {
		return (_, row): boolean =>
			applyColumnQuickFilter(value, row, (value: string, row: DispatchLeg) => {
				const { custName, csnCustRefNo } = row;
				return caseInsensitiveIncludes(value, [custName, csnCustRefNo]);
			});
	},
	valueGetter: (_, row: DispatchLeg) => `${row.custName ?? ''}|||${row.csnCustRefNo ?? ''}`,
	sortable: true,
	sortComparator: (v1, v2, cellParams1) => {
		if (typeof v1 === 'string' && typeof v2 === 'string') {
			return defaultSortComparator(v1.split('|||')[0], v2.split('|||')[0], cellParams1);
		}

		return 0;
	}
};

export default customerDefinition;
